/**
 * ConventionsBrowser — Browse all language conventions.
 *
 * Lists all 70 languages with key convention properties.
 * Filterable by family, script, writing direction.
 * Click to view full convention detail.
 *
 * Route: /polyglot/conventions
 * API: GET /@polyglot/conventions
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Loader,
  Icon,
  Table,
  Label,
  Input,
  Breadcrumb,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

export default function ConventionsBrowser() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/conventions`)
      .then((json) => {
        setData(json.items || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading conventions..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '2em' }}>
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const filtered = search
    ? data.filter((l) =>
        `${l.language_code} ${l.language_name_english} ${l.language_name_native} ${l.language_family || ''}`
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
    : data;

  // Group by family
  const families = {};
  filtered.forEach((l) => {
    const fam = l.language_family || 'Other';
    if (!families[fam]) families[fam] = [];
    families[fam].push(l);
  });
  const sortedFamilies = Object.keys(families).sort();

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '1100px' }}>
      <Helmet title="Language Conventions" />

      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Conventions</Breadcrumb.Section>
      </Breadcrumb>

      <div className="polyglot-page-header">
        <h1>Language Conventions</h1>
        <p className="subtitle">
          {data.length} languages — register, script, grammar, plurals, review status
        </p>
      </div>

      <Input
        fluid
        icon="search"
        placeholder="Filter by language name, code, or family..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: '1.5em' }}
      />

      {sortedFamilies.map((family) => (
        <div key={family} style={{ marginBottom: '1.5em' }}>
          <Header as="h3" style={{ marginBottom: '0.5em' }}>
            {family}
            <Label size="small" color="grey" style={{ marginLeft: '0.5em', fontWeight: 'normal' }}>
              {families[family].length} {families[family].length === 1 ? 'language' : 'languages'}
            </Label>
          </Header>
          <Table compact celled selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: '60px' }}>Code</Table.HeaderCell>
                <Table.HeaderCell>Language</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '80px' }}>Script</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '50px' }}>Dir</Table.HeaderCell>
                <Table.HeaderCell>Register</Table.HeaderCell>
                <Table.HeaderCell>Button style</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={{ width: '60px' }}>Grammar</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={{ width: '60px' }}>Plurals</Table.HeaderCell>
                <Table.HeaderCell textAlign="center" style={{ width: '70px' }}>Review</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {families[family].map((l) => (
                <Table.Row key={l.language_code}>
                  <Table.Cell>
                    <Link to={`/polyglot/conventions/${l.language_code}`}>
                      <strong>{l.language_code}</strong>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <Link to={`/polyglot/conventions/${l.language_code}`}>
                      {l.language_name_english}
                    </Link>
                    {l.language_name_native !== l.language_name_english && (
                      <span style={{ color: '#888', marginLeft: '0.5em' }}>
                        {l.language_name_native}
                      </span>
                    )}
                  </Table.Cell>
                  <Table.Cell>{l.script}</Table.Cell>
                  <Table.Cell>
                    {l.writing_direction === 'rtl' ? (
                      <Label size="mini" color="orange">RTL</Label>
                    ) : (
                      <span style={{ color: '#ccc' }}>LTR</span>
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em' }}>
                    {l.register || <span style={{ color: '#ccc' }}>&mdash;</span>}
                  </Table.Cell>
                  <Table.Cell style={{ fontSize: '0.9em' }}>
                    {l.button_style || <span style={{ color: '#ccc' }}>&mdash;</span>}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {l.grammar_patterns > 0 ? (
                      <Label size="mini" circular>{l.grammar_patterns}</Label>
                    ) : (
                      <span style={{ color: '#ccc' }}>&mdash;</span>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {l.plural_rules > 0 ? (
                      <Label size="mini" circular color="teal">{l.plural_rules}</Label>
                    ) : (
                      <span style={{ color: '#ccc' }}>&mdash;</span>
                    )}
                  </Table.Cell>
                  <Table.Cell textAlign="center">
                    {l.review_status === 'reviewed' ? (
                      <Label size="mini" color="green">reviewed</Label>
                    ) : (
                      <Label size="mini" basic color="grey">pending</Label>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ))}
    </Container>
  );
}
